/*******************************************************************************
 * Copyright     : Copyright (c)  2024 Honeywell Inc. All rights reserved.
 *                 This work contains trade secrets and confidential materials
 *                 of Honeywell Inc.No part may be reproduced or transmitted in any form by any
 *                 means or for anypurpose without the express written permission of Honeywell. *
 *******************************************************************************/
import React, { useContext, useEffect, useState } from "react";
import { useNavigateToLandingPage } from "../../utils/navigationUtils";
import {
  ArrowLeftIcon,
  Button,
  Card,
  CardContent,
  Control,
  Label,
  Modal,
  PageLoader,
  Select,
  SelectOption,
  SelectOptionSearch,
  ToastContext
} from "@forge/common";
import { useSelector } from "react-redux";
import "./index.scss";
import { GatewaySiteProps } from "../../models/devicelist";
//@Store
import { RootState } from "../../store";
import { gatewayErrorStatusMessages, validateInput } from "../../utils/utility";
import { useGetOrgMetadataQuery, useGetOrgSiteListsQuery } from "../../services/rbm-api";
import { ISiteListProps } from "../../models";
import SiteTable from "./SiteTable";
import { ConfigurationVariables } from "../../config";
import { LOCALE_CONST, orgStatusLists } from "../../utils/constants";
import { useSiteBulkAssignmentMutation } from "../../services/api";
import { useTranslation } from "react-i18next";

const BlukAssignment = () => {
  const { t } = useTranslation();
  const { addSuccess, addError } = useContext(ToastContext);
  const orgLists = useSelector((state: RootState) => state.global.orgLists);
  const [
    siteBulkAssignment,
    {
      error: siteBulkAssignmentErrorData,
      isLoading: siteBulkAssignmentLoading,
      isSuccess: siteBulkAssignmentSuccess,
      isError: siteBulkAssignmentError
    }
  ] = useSiteBulkAssignmentMutation();
  const { navigateToLandingPage } = useNavigateToLandingPage();
  const [selectedOrgName, setSelectedOrgName] = useState<string>('');
  const [selectedOrgValId, setSelectedOrgValId] = useState<string>('');
  const [orgSearchQuery, setOrgSearchQuery] = useState<string>("");
  const [siteLists, setSiteLists] = useState<GatewaySiteProps[]>([]);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [selectedSite, setSelectedSite] = useState<ISiteListProps[]>([]);
  const [orgStatusVals, setOrgStatusVals] = useState<{ [key: string]: string }>(
    {
      failedStatus: "",
      completedStatus: "",
      inProgressStatus: "",
      onboardingStatus: ""
    }
  );
  const [requestQbj, setRequestQbj] = useState<any>({
    organizationId: "",
    organizationName: "",
    deviceId: [],
    site: []
  });
  
  const {
    data: orgMetaData,
    isLoading: orgMetaLoading,
    isFetching: orgMetaFetching
  } = useGetOrgMetadataQuery(
    { orgId: selectedOrgValId },
    { skip: !selectedOrgValId }
  );

  const getOnBoardStatus = () =>
    orgMetaData?.find(
      (item: { key: string }) => item.key === orgStatusVals?.onboardingStatus
    )?.value || "";

  const orgSiteSkipFn = () => {
    if (!selectedOrgValId) {
      return true;
    } else if (getOnBoardStatus().includes(orgStatusVals?.completedStatus)) {
      return false;
    } else {
      return true;
    }
  };

  const {
    data: siteDataLists,
    isLoading: siteLoading,
    isFetching: siteFetching,
    refetch: siteRefresh
  } = useGetOrgSiteListsQuery(
    { orgId: selectedOrgValId },
    { skip: orgSiteSkipFn() }   
  );

  useEffect(() => {
    let orgStatusObj: { [key: string]: string } = {};

    ConfigurationVariables.ORG_STATUS_LISTS?.split(" ").forEach(
      (item: string, index: number) => {
        orgStatusObj[orgStatusLists[index]] = item;
      }
    );

    setOrgStatusVals(orgStatusObj);
  }, []);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const getOrgName = urlParams.get("org");
    const getOrgId = urlParams.get("orgId");
    const getDeviceId = urlParams.get("ids");
    const deviceIds = getDeviceId ? getDeviceId.split(",") : [];
    setSelectedOrgValId(getOrgId);
    setSelectedOrgName(getOrgName);    
    setRequestQbj({
      ...requestQbj,
      organizationId: getOrgId,
      organizationName: getOrgName,
      deviceId: deviceIds
    });
  }, [window.location]);

  useEffect(() => {
    if (siteDataLists?.resources?.length > 0) {
      const siteValLists: ISiteListProps[] = [];
      siteDataLists?.resources?.forEach((val: ISiteListProps) => {
        if (val?.type === "Site") {          
          siteValLists.push({
            name: val?.name || "",
            id: val?.id || "",
            metadata: val?.metadata || [],
            isChecked: false,
            type: val?.type || "",
            modelId: val?.modelId || "",
            externalId: val?.externalId || ""
          });
        }
      });
      setSiteLists(
        [...siteValLists].sort((a, b) => a.name.localeCompare(b.name))
      );
    } else {
      setSiteLists([]);
    }
  }, [siteDataLists, selectedOrgValId]);

  const handleSiteSelect = (selecSite: any) => {
    setSelectedSite(selecSite);
    setRequestQbj({
      ...requestQbj,
      site: selecSite
    });    
  };

  // Calling the Api to assign the sites =======
  const handleConfirmation = () => {
    siteBulkAssignment(requestQbj);
    setIsConfirmationModalOpen(false);
  };

  useEffect(() => {
    if (!siteBulkAssignmentLoading) {
      if (siteBulkAssignmentSuccess) {
        addSuccess({
          id: 'uop-toast-message',
          className: 'uop-toast-message',
          title: '',
          subtitle: '',
          showClose: true,
          showDismiss: false,
          content: t(LOCALE_CONST.siteAssignedSuccess),
          lifetime: 5000
        });
        navigateToLandingPage();
      }
      if (siteBulkAssignmentError) {
        const convertFn = (val: string) => t(val);

        addError({
          id: 'uop-toast-message',
          className: 'uop-toast-message',
          title: '',
          subtitle: '',
          showClose: true,
          showDismiss: false,
          content: gatewayErrorStatusMessages(
            convertFn,
            t(LOCALE_CONST.assignSitesError),
            (siteBulkAssignmentErrorData && 'status' in siteBulkAssignmentErrorData)
              ? (siteBulkAssignmentErrorData as any).status
              : undefined
          ),
          lifetime: 5000
        }); 
      }       
    }
  }, [siteBulkAssignmentSuccess, siteBulkAssignmentLoading, siteBulkAssignmentError, siteBulkAssignmentErrorData]);  

  return (
    <>
      {siteBulkAssignmentLoading && <PageLoader  content={`${t(LOCALE_CONST.loading)}...`} isLoading />}      
      <section className="bulk-assignment mx-auto">
        <Button
          icon={<ArrowLeftIcon strokeWidth={2} />}
          iconPosition="left"
          size="regular"
          variant="quiet-primary"
          className="p-0 bulk-assignment-back-button"
          onClick={navigateToLandingPage}
        >
          {t(LOCALE_CONST.backToGateways)}
        </Button>
        <h2 className="uppercase"> {t(LOCALE_CONST.bulkAssignment)}: </h2>
        <h3 className="bulk-assignment-title pb-[16px]">{t(LOCALE_CONST.sites)}</h3>
        <Card>
          <CardContent>
            <h3>{t(LOCALE_CONST.registrationDetails)}</h3>
            <Control className="w-96">
              <Label className="font-normal"> {t(LOCALE_CONST.organization)} </Label>
              <Select
                id="organization"
                name="organization"
                placeholder={
                  selectedOrgName !== "null"
                    ? selectedOrgName
                    : t(LOCALE_CONST.selectOrg)
                }
                required
                className="w-64"
                onChange={(e) => {
                  setSelectedOrgValId(e.target.value);
                  siteRefresh();
                }}
                disabled
                search={
                  <SelectOptionSearch
                    label={t(LOCALE_CONST.searchOrg)}
                    onChange={(e) => {
                      if (validateInput(e.target.value)) {
                        setOrgSearchQuery(e.target.value);
                      }
                    }}
                    placeholder={t(LOCALE_CONST.searchOrg)}
                    value={orgSearchQuery}
                  />
                }
              >
                {orgLists.map((org) => (
                  <SelectOption
                    key={org.value}
                    value={org.value}
                    label={org.text}
                  />
                ))}
              </Select>
            </Control>
            <div>
              <div className="bulk-assignment-site-table">
                <SiteTable
                  data={siteLists}
                  isDisabled={Boolean(
                    siteLists.length <= 0 || !selectedOrgValId
                  )}
                  siteLoading={siteLoading}
                  title={t(LOCALE_CONST.associatedSites)}
                  siteFetching={siteFetching}
                  orgMetaFetching={orgMetaFetching}
                  orgMetaLoading={orgMetaLoading}
                  onSiteSelect={handleSiteSelect}
                />
              </div>
            </div>
          </CardContent>
        </Card>
      </section>
      <div className="edit-device-buttons-block-cls mx-auto">
        <Button variant="secondary" className="cancel-btn-cls" onClick={navigateToLandingPage}>
          {t(LOCALE_CONST.cancel)}
        </Button>
        <Button
          variant="primary"
          disabled={selectedSite.length <= 0}
          onClick={() => setIsConfirmationModalOpen(true)}
        >
          {t(LOCALE_CONST.create)}
        </Button>
      </div>
      {/* Confirmation Modal */}
      {isConfirmationModalOpen && (
        <Modal
          backdrop
          onModalClose={() => setIsConfirmationModalOpen(false)}
          title={t(LOCALE_CONST.overwriteSites)}
          footerRight={
            <div>
              <Button 
                variant="secondary"
                onClick={() => setIsConfirmationModalOpen(false)}
              >
                {t(LOCALE_CONST.cancel)}
              </Button>
              <Button
                variant="destructive-primary"
                className="bulk-assignment-confirm-button ml-[12px]"
                onClick={handleConfirmation}
              >
                {t(LOCALE_CONST.confirm)}
              </Button>
            </div>
          }
          className="w-[400px]"
        >
          {t(LOCALE_CONST.overwrittenAssignSiteMap)}
        </Modal>
      )}
      {/* Modal End */}      
    </>
  );
};

export default BlukAssignment;